import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useDataStore } from "@/lib/store/data-store";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData } from "@/lib/types/weapon";
import { ComparisonSettingsDrawer } from "@/components/UnitComparison/ComparisonSettingsDrawer";
import { ComparisonDisplay } from "@/components/UnitComparison/ComparisonDisplay";
import { GitCompare } from "lucide-react";
import { RelatedTools } from "@/components/RelatedTools";

// Basic weapon stats to auto-include when a weapon is selected
const BASIC_WEAPON_STATS = [
  "weapon_penetration",
  "weapon_heDamage",
  "weapon_groundMaxRange",
  "weapon_staticAccuracy",
  "weapon_movingAccuracy",
  "weapon_rateOfFire",
];

export function UnitComparison() {
  const [searchParams] = useSearchParams();
  const {
    units,
    weapons,
    divisions,
    loadingUnits,
    getWeaponByDescriptor,
    getUnitByDescriptor,
  } = useDataStore();
  const [selectedUnits, setSelectedUnits] = useState<Unit[]>([]);
  const [selectedStats, setSelectedStats] = useState<string[]>([]);
  const [selectedWeapons, setSelectedWeapons] = useState<Map<string, number[]>>(new Map());
  const [weaponDataMap, setWeaponDataMap] = useState<Map<string, WeaponData[]>>(new Map());
  const [urlParamProcessed, setUrlParamProcessed] = useState(false);

  // Load weapon data for selected units
  useEffect(() => {
    const newWeaponDataMap = new Map<string, WeaponData[]>();

    for (const unit of selectedUnits) {
      if (unit.weaponDescriptorName) {
        const weaponData = getWeaponByDescriptor(unit.weaponDescriptorName);
        if (weaponData && weaponData.weapons) {
          newWeaponDataMap.set(unit.weaponDescriptorName, weaponData.weapons);
        }
      }
    }

    setWeaponDataMap(newWeaponDataMap);
  }, [selectedUnits, getWeaponByDescriptor]);

  // Initialize first weapon as selected when a unit is added or weapon data changes
  useEffect(() => {
    setSelectedWeapons((prev) => {
      const newMap = new Map(prev);
      let hasChanges = false;

      for (const unit of selectedUnits) {
        if (unit.weaponDescriptorName) {
          const weapons = weaponDataMap.get(unit.weaponDescriptorName);
          if (weapons && weapons.length > 0) {
            const currentSelection = newMap.get(unit.descriptorName);
            // If unit has no selection yet, initialize with first weapon only
            if (!currentSelection || currentSelection.length === 0) {
              newMap.set(unit.descriptorName, [0]);
              hasChanges = true;
            }
          }
        }
      }

      return hasChanges ? newMap : prev;
    });
  }, [selectedUnits, weaponDataMap]);

  // Handle URL parameter for pre-selecting a unit
  useEffect(() => {
    if (urlParamProcessed || !units || loadingUnits) {
      return;
    }

    const unitParam = searchParams.get("unit");
    if (unitParam) {
      const decodedUnitDescriptor = decodeURIComponent(unitParam);
      const unit = getUnitByDescriptor(decodedUnitDescriptor);
      
      if (unit) {
        setSelectedUnits([unit]);
        // Auto-select basic stats for better UX
        setSelectedStats(BASIC_WEAPON_STATS);
        setUrlParamProcessed(true);
      }
    } else {
      setUrlParamProcessed(true);
    }
  }, [searchParams, units, loadingUnits, getUnitByDescriptor, urlParamProcessed]);

  const handleSelectUnit = (unit: Unit) => {
    setSelectedUnits((prev) => [...prev, unit]);
  };

  const handleRemoveUnit = (unit: Unit) => {
    setSelectedUnits((prev) => prev.filter((u) => u.descriptorName !== unit.descriptorName));
    // Remove weapon selection for this unit
    setSelectedWeapons((prev) => {
      const newMap = new Map(prev);
      newMap.delete(unit.descriptorName);
      return newMap;
    });
  };

  const handleWeaponSelect = (unitDescriptor: string, weaponIndexes: number[]) => {
    setSelectedWeapons((prev) => {
      const newMap = new Map(prev);
      if (weaponIndexes.length === 0) {
        // Ensure at least one weapon is selected - get all weapons for this unit
        const unit = selectedUnits.find((u) => u.descriptorName === unitDescriptor);
        if (unit?.weaponDescriptorName) {
          const weapons = weaponDataMap.get(unit.weaponDescriptorName);
          if (weapons && weapons.length > 0) {
            const allWeaponIndexes = weapons.map((_, index) => index);
            newMap.set(unitDescriptor, allWeaponIndexes);
          }
        }
      } else {
        newMap.set(unitDescriptor, weaponIndexes);
      }
      return newMap;
    });

    // Auto-include basic weapon stats when weapons are selected
    if (weaponIndexes.length > 0) {
      setSelectedStats((prev) => {
        const newStats = new Set(prev);
        BASIC_WEAPON_STATS.forEach((stat) => {
          newStats.add(stat);
        });
        return Array.from(newStats);
      });
    }
  };

  const handleStatsChange = (stats: string[]) => {
    setSelectedStats(stats);
  };

  const divisionNameMap = useMemo(() => {
    const m = new Map<string, string>();
    (divisions || []).forEach((d) => m.set(d.descriptor, d.name));
    return m;
  }, [divisions]);

  const firstSelected = selectedUnits[0];
  const firstDeckBuilderTargets = useMemo(() => {
    if (!firstSelected?.divisions?.length) return undefined;
    return firstSelected.divisions.map((d) => ({
      descriptor: d,
      name: divisionNameMap.get(d) ?? d,
    }));
  }, [firstSelected, divisionNameMap]);

  if (loadingUnits) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto py-4">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="container max-w-full w-full py-1 pb-8 lg:pr-[400px]">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <GitCompare className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Unit Comparison</h1>
        </div>
        {firstSelected ? (
          <RelatedTools
            variant="compact"
            className="mb-4"
            unitDescriptor={firstSelected.descriptorName}
            deckBuilderTargets={firstDeckBuilderTargets}
          />
        ) : null}

        {/* Comparison Display */}
        {selectedUnits.length > 0 && selectedStats.length > 0 ? (
          <ComparisonDisplay
            units={selectedUnits}
            selectedStats={selectedStats}
            weaponData={weaponDataMap}
            selectedWeapons={selectedWeapons}
            onWeaponSelect={handleWeaponSelect}
          />
        ) : (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-muted-foreground">
                <p>Select units and stats in the settings panel to start comparing</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Settings Drawer */}
      <ComparisonSettingsDrawer
        selectedUnits={selectedUnits}
        onSelectUnit={handleSelectUnit}
        onRemoveUnit={handleRemoveUnit}
        weaponData={weaponDataMap}
        selectedWeapons={selectedWeapons}
        onWeaponSelect={handleWeaponSelect}
        selectedStats={selectedStats}
        onStatsChange={handleStatsChange}
      />
    </div>
  );
}
